// EthosinoArchitectureSection.jsx

import React, {
  useMemo,
  useState
} from "react";

import {
  AnimatePresence,
  motion,
  useReducedMotion
} from "framer-motion";

import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Banknote,
  Bot,
  Boxes,
  BrainCircuit,
  Check,
  CircleDollarSign,
  Database,
  Fingerprint,
  Gamepad2,
  Gauge,
  Globe2,
  Layers3,
  LockKeyhole,
  Network,
  Radar,
  Route,
  ShieldCheck,
  Sparkles,
  Users,
  WalletCards,
  Waypoints,
  Zap
} from "lucide-react";

import {
  Link
} from "react-router-dom";

import "./Styles.css";


/* ============================================================
   ARCHITECTURE DATA

   Keep this data-driven. As Ethosino expands, the visual can
   expand without rewriting the section architecture.
============================================================ */

const architectureLayers = [

  {
    id:
      "experience",

    number:
      "01",

    kicker:
      "PLAYER + GROWTH",

    title:
      "Experience & Acquisition",

    shortTitle:
      "Player Experience",

    icon:
      Users,

    accent:
      "cyan",

    headline:
      "Turn traffic into an operator-owned player relationship.",

    description:
      "The player-facing casino, account experience, rewards, promotions and acquisition systems live inside the same operating stack instead of being stitched together after launch.",

    capabilities: [
      "Branded player casino",
      "Promotions and rewards",
      "Referral and affiliate systems",
      "Player account lifecycle",
      "Multi-brand presentation"
    ],

    signals: [
      {
        label:
          "ENTRY",

        value:
          "Domain + Brand"
      },

      {
        label:
          "IDENTITY",

        value:
          "Player Account"
      },

      {
        label:
          "GROWTH",

        value:
          "Rewards + Referral"
      }
    ],

    output:
      "Acquired users become attributable players inside the operator environment."
  },


  {
    id:
      "tenant",

    number:
      "02",

    kicker:
      "TENANT CONTROL",

    title:
      "Brand & Operator Boundary",

    shortTitle:
      "Tenant Control",

    icon:
      Layers3,

    accent:
      "blue",

    headline:
      "Shared engineering without flattening every operator into the same casino.",

    description:
      "Ethosino separates the maintained platform from the operator identity. Tenant context carries brand, domain, configuration, catalog access and operational policy through the platform.",

    capabilities: [
      "Tenant-aware routing",
      "Independent domains",
      "Theme and branding overrides",
      "Game catalog configuration",
      "Operator-scoped controls"
    ],

    signals: [
      {
        label:
          "SCOPE",

        value:
          "Tenant Context"
      },

      {
        label:
          "IDENTITY",

        value:
          "Independent"
      },

      {
        label:
          "CORE",

        value:
          "Shared"
      }
    ],

    output:
      "One maintained platform can support distinct operator environments."
  },


  {
    id:
      "games",

    number:
      "03",

    kicker:
      "FIRST-PARTY PRODUCT",

    title:
      "Game Runtime & Fairness",

    shortTitle:
      "Game Engine",

    icon:
      Gamepad2,

    accent:
      "violet",

    headline:
      "The game layer is infrastructure, not just inventory.",

    description:
      "Ethosino's first-party games connect game state, deterministic fairness inputs, session context and settlement references into the broader operator platform.",

    capabilities: [
      "35+ first-party originals",
      "Table and instant games",
      "Poker tournament systems",
      "Provably-fair verification",
      "Versioned game logic"
    ],

    signals: [
      {
        label:
          "ROUND",

        value:
          "Deterministic"
      },

      {
        label:
          "FAIRNESS",

        value:
          "Verifiable"
      },

      {
        label:
          "CATALOG",

        value:
          "Tenant Scoped"
      }
    ],

    output:
      "Game outcomes become attributable events that can be inspected, analyzed and settled."
  },


  {
    id:
      "money",

    number:
      "04",

    kicker:
      "MONEY MOVEMENT",

    title:
      "Wallet, Ledger & Treasury",

    shortTitle:
      "Money + Ledger",

    icon:
      WalletCards,

    accent:
      "green",

    headline:
      "Every important financial movement should have a system of record.",

    description:
      "Wallet state, game settlement, transaction history and operator treasury reporting are designed as connected financial primitives rather than disconnected balance fields.",

    capabilities: [
      "Player wallet infrastructure",
      "Transaction accounting",
      "Game settlement linkage",
      "Operator treasury visibility",
      "Configurable currency modes"
    ],

    signals: [
      {
        label:
          "PLAYER",

        value:
          "Wallet"
      },

      {
        label:
          "HISTORY",

        value:
          "Ledger"
      },

      {
        label:
          "OPERATOR",

        value:
          "Treasury"
      }
    ],

    output:
      "Financial state stays connected to the events that created it."
  },


  {
    id:
      "trust",

    number:
      "05",

    kicker:
      "TRUST + CONTROLS",

    title:
      "Security, Risk & Compliance Tooling",

    shortTitle:
      "Trust Layer",

    icon:
      ShieldCheck,

    accent:
      "amber",

    headline:
      "Operational trust needs controls, evidence and clear boundaries.",

    description:
      "Ethosino provides the infrastructure operators use to enforce their configured posture: security events, geolocation controls, risk signals, audit records, responsible-gaming surfaces and integration points for identity verification.",

    capabilities: [
      "Geolocation controls",
      "Security event monitoring",
      "Audit and change history",
      "KYC / AML integration points",
      "Responsible-gaming controls"
    ],

    signals: [
      {
        label:
          "ACCESS",

        value:
          "Scoped"
      },

      {
        label:
          "EVENTS",

        value:
          "Auditable"
      },

      {
        label:
          "POLICY",

        value:
          "Configurable"
      }
    ],

    output:
      "Operator policy becomes enforceable platform behavior with evidence behind it."
  },


  {
    id:
      "intelligence",

    number:
      "06",

    kicker:
      "OPERATOR INTELLIGENCE",

    title:
      "Analytics & AI Operator Copilot",

    shortTitle:
      "Intelligence",

    icon:
      BrainCircuit,

    accent:
      "pink",

    headline:
      "Ask the operating system what deserves attention.",

    description:
      "Operational data becomes useful when an operator can interpret it quickly. Ethosino combines reporting, evidence and permissioned AI-assisted analysis without giving the model transactional authority.",

    capabilities: [
      "Revenue and player analytics",
      "Game performance analysis",
      "Risk and audit summaries",
      "Tenant-scoped AI queries",
      "Evidence-backed recommendations"
    ],

    signals: [
      {
        label:
          "MODE",

        value:
          "Read Only"
      },

      {
        label:
          "SCOPE",

        value:
          "Permissioned"
      },

      {
        label:
          "OUTPUT",

        value:
          "Advisory"
      }
    ],

    output:
      "Operational evidence becomes faster to interpret without handing the AI control of the casino."
  }

];


const architecturePrinciples = [

  {
    icon:
      Network,

    label:
      "SHARED CORE",

    value:
      "Maintain once"
  },

  {
    icon:
      Fingerprint,

    label:
      "TENANT CONTEXT",

    value:
      "Scope everywhere"
  },

  {
    icon:
      Database,

    label:
      "SYSTEM OF RECORD",

    value:
      "Events + ledgers"
  },

  {
    icon:
      BadgeCheck,

    label:
      "EVIDENCE",

    value:
      "Explain what happened"
  }

];


/* ============================================================
   REVEAL WRAPPER
============================================================ */

const ArchReveal = ({
  children,
  direction = "left",
  delay = 0,
  className = ""
}) => {

  const reduceMotion =
    useReducedMotion();


  const offset =

    direction === "left"
      ? -90

      : direction === "right"
        ? 90

        : 0;


  return (

    <motion.div
      className={
        className
      }

      initial={
        reduceMotion
          ? false
          : {
              opacity:
                0,

              x:
                offset,

              y:
                direction === "up"
                  ? 55
                  : 0,

              rotateY:
                direction === "left"
                  ? 5
                  : direction === "right"
                    ? -5
                    : 0
            }
      }

      whileInView={{
        opacity:
          1,

        x:
          0,

        y:
          0,

        rotateY:
          0
      }}

      viewport={{
        once:
          true,

        amount:
          0.18
      }}

      transition={{
        duration:
          reduceMotion
            ? 0
            : 0.72,

        delay,

        ease: [
          0.16,
          1,
          0.3,
          1
        ]
      }}
    >

      {
        children
      }

    </motion.div>

  );

};


/* ============================================================
   SPOTLIGHT CARD
============================================================ */

const ArchitectureLayerButton = ({
  layer,
  active,
  index,
  onSelect
}) => {

  const Icon =
    layer.icon;


  const handleMouseMove =
    event => {

      const rect =
        event.currentTarget.getBoundingClientRect();


      event.currentTarget.style.setProperty(
        "--eth-arch-v5-mouse-x",
        `${event.clientX - rect.left}px`
      );


      event.currentTarget.style.setProperty(
        "--eth-arch-v5-mouse-y",
        `${event.clientY - rect.top}px`
      );

    };


  return (

    <motion.button
      type="button"

      className={[
        "eth-arch-v5-layer-button",
        `eth-arch-v5-layer-button--${layer.accent}`,
        active
          ? "is-active"
          : ""
      ].join(
        " "
      )}

      onClick={() =>
        onSelect(
          index
        )
      }

      onMouseMove={
        handleMouseMove
      }

      whileHover={{
        x:
          4
      }}

      whileTap={{
        scale:
          0.985
      }}
    >

      <div className="eth-arch-v5-layer-spotlight" />


      <div className="eth-arch-v5-layer-button-number">

        {
          layer.number
        }

      </div>


      <div className="eth-arch-v5-layer-button-icon">

        <Icon
          size={
            18
          }
          strokeWidth={
            1.8
          }
        />

      </div>


      <div className="eth-arch-v5-layer-button-copy">

        <span>
          {
            layer.kicker
          }
        </span>

        <strong>
          {
            layer.shortTitle
          }
        </strong>

      </div>


      <div className="eth-arch-v5-layer-button-arrow">

        <ArrowRight
          size={
            15
          }
        />

      </div>

    </motion.button>

  );

};


/* ============================================================
   COMPONENT
============================================================ */

const EthosinoArchitectureSection = () => {

  const cards =
    useMemo(
      () =>
        architectureLayers,
      []
    );


  const [
    activeIndex,
    setActiveIndex
  ] =
    useState(
      2
    );


  const activeLayer =
    cards[
      activeIndex
    ];


  const ActiveIcon =
    activeLayer.icon;


  const reduceMotion =
    useReducedMotion();


  return (

    <section
      className="eth-arch-v5-section"
      id="architecture"
    >

      {/* ======================================================
          BACKGROUND
      ====================================================== */}

      <div
        className="eth-arch-v5-background"
        aria-hidden="true"
      >

        <div className="eth-arch-v5-background-grid" />

        <div className="eth-arch-v5-background-scan" />

        <div className="eth-arch-v5-background-orb eth-arch-v5-background-orb--a" />

        <div className="eth-arch-v5-background-orb eth-arch-v5-background-orb--b" />

        <div className="eth-arch-v5-background-orb eth-arch-v5-background-orb--c" />

      </div>


      <div className="eth-arch-v5-shell">

        {/* ====================================================
            HEADER
        ==================================================== */}

        <ArchReveal
          direction="left"
          className="eth-arch-v5-header"
        >

          <div className="eth-arch-v5-header-copy">

            <div className="eth-arch-v5-eyebrow">

              <span className="eth-arch-v5-eyebrow-signal">

                <span />

              </span>

              PLATFORM ARCHITECTURE

            </div>


            <h2>

              One operating system.

              <span>
                {" "}
                Six layers of control.
              </span>

            </h2>


            <p>

              Ethosino is not a casino skin sitting on top of disconnected
              vendors. The player product, tenant layer, first-party games,
              wallet and ledger infrastructure, trust controls and operator
              intelligence are designed to operate as one system.

            </p>

          </div>


          <div className="eth-arch-v5-header-principle">

            <span>
              SYSTEM PRINCIPLE
            </span>


            <strong>
              Own the operating layer.
            </strong>


            <p>
              The operator should be able to understand where activity came
              from, how it was processed, what changed financial state and what
              evidence exists afterward.
            </p>

          </div>

        </ArchReveal>



        {/* ====================================================
            PRINCIPLE STRIP
        ==================================================== */}

        <ArchReveal
          direction="right"
          delay={
            0.08
          }
          className="eth-arch-v5-principle-strip"
        >

          {
            architecturePrinciples.map(
              (
                principle,
                index
              ) => {

                const Icon =
                  principle.icon;


                return (

                  <div
                    className="eth-arch-v5-principle"
                    key={
                      principle.label
                    }
                  >

                    <div className="eth-arch-v5-principle-index">

                      0{
                        index + 1
                      }

                    </div>


                    <div className="eth-arch-v5-principle-icon">

                      <Icon
                        size={
                          17
                        }
                      />

                    </div>


                    <div>

                      <span>
                        {
                          principle.label
                        }
                      </span>

                      <strong>
                        {
                          principle.value
                        }
                      </strong>

                    </div>

                  </div>

                );

              }
            )
          }

        </ArchReveal>



        {/* ====================================================
            INTERACTIVE ARCHITECTURE EXPLORER
        ==================================================== */}

        <div className="eth-arch-v5-explorer">

          {/* ==================================================
              LEFT CONTROL RAIL
          ================================================== */}

          <ArchReveal
            direction="left"
            className="eth-arch-v5-layer-rail"
          >

            <div className="eth-arch-v5-panel-heading">

              <div>

                <span>
                  ARCHITECTURE MAP
                </span>

                <strong>
                  Select a system layer
                </strong>

              </div>


              <div className="eth-arch-v5-panel-status">

                <i />

                ONLINE

              </div>

            </div>


            <div className="eth-arch-v5-layer-buttons">

              {
                cards.map(
                  (
                    layer,
                    index
                  ) => (

                    <ArchitectureLayerButton
                      key={
                        layer.id
                      }

                      layer={
                        layer
                      }

                      index={
                        index
                      }

                      active={
                        index ===
                        activeIndex
                      }

                      onSelect={
                        setActiveIndex
                      }
                    />

                  )
                )
              }

            </div>


            <div className="eth-arch-v5-layer-rail-footer">

              <Waypoints
                size={
                  17
                }
              />


              <div>

                <span>
                  CONNECTED ARCHITECTURE
                </span>

                <small>
                  Each layer feeds the operating context around it.
                </small>

              </div>

            </div>

          </ArchReveal>



          {/* ==================================================
              CENTER CORE VISUAL
          ================================================== */}

          <ArchReveal
            direction="up"
            delay={
              0.1
            }
            className="eth-arch-v5-core-stage"
          >

            <div className="eth-arch-v5-core-stage-grid" />


            <div className="eth-arch-v5-core-stage-label">

              <span>
                ETHOSINO CONTROL PLANE
              </span>

              <b>
                SYSTEM MAP / LIVE
              </b>

            </div>


            <div className="eth-arch-v5-core-visual">

              <motion.div
                className="eth-arch-v5-orbit eth-arch-v5-orbit--outer"

                animate={
                  reduceMotion
                    ? {}
                    : {
                        rotate:
                          360
                      }
                }

                transition={{
                  duration:
                    34,

                  repeat:
                    Infinity,

                  ease:
                    "linear"
                }}
              />


              <motion.div
                className="eth-arch-v5-orbit eth-arch-v5-orbit--middle"

                animate={
                  reduceMotion
                    ? {}
                    : {
                        rotate:
                          -360
                      }
                }

                transition={{
                  duration:
                    25,

                  repeat:
                    Infinity,

                  ease:
                    "linear"
                }}
              />


              <div className="eth-arch-v5-orbit eth-arch-v5-orbit--inner" />


              {/* ==============================================
                  ORBIT NODES
              ============================================== */}

              {
                cards.map(
                  (
                    layer,
                    index
                  ) => {

                    const Icon =
                      layer.icon;


                    return (

                      <motion.button
                        type="button"

                        aria-label={
                          layer.title
                        }

                        key={
                          layer.id
                        }

                        className={[
                          "eth-arch-v5-orbit-node",
                          `eth-arch-v5-orbit-node--${index + 1}`,
                          `eth-arch-v5-orbit-node--${layer.accent}`,
                          activeIndex === index
                            ? "is-active"
                            : ""
                        ].join(
                          " "
                        )}

                        onClick={() =>
                          setActiveIndex(
                            index
                          )
                        }

                        animate={
                          reduceMotion
                            ? {}
                            : {
                                y: [
                                  0,
                                  index % 2 === 0
                                    ? -5
                                    : 5,
                                  0
                                ]
                              }
                        }

                        transition={{
                          duration:
                            3.7 +
                            index *
                            0.18,

                          repeat:
                            Infinity,

                          ease:
                            "easeInOut"
                        }}
                      >

                        <Icon
                          size={
                            17
                          }
                        />


                        <span>
                          {
                            layer.number
                          }
                        </span>

                      </motion.button>

                    );

                  }
                )
              }


              {/* ==============================================
                  TRAVELING SIGNALS
              ============================================== */}

              <span className="eth-arch-v5-data-signal eth-arch-v5-data-signal--one" />

              <span className="eth-arch-v5-data-signal eth-arch-v5-data-signal--two" />

              <span className="eth-arch-v5-data-signal eth-arch-v5-data-signal--three" />


              {/* ==============================================
                  CORE
              ============================================== */}

              <motion.div
                className="eth-arch-v5-core"

                animate={
                  reduceMotion
                    ? {}
                    : {
                        boxShadow: [
                          "0 0 30px rgba(0,255,213,.06)",
                          "0 0 70px rgba(0,255,213,.18)",
                          "0 0 30px rgba(0,255,213,.06)"
                        ]
                      }
                }

                transition={{
                  duration:
                    3,

                  repeat:
                    Infinity
                }}
              >

                <span className="eth-arch-v5-core-pulse" />


                <div className="eth-arch-v5-core-logo">

                  <Boxes
                    size={
                      27
                    }
                    strokeWidth={
                      1.5
                    }
                  />

                </div>


                <small>
                  ETHOSINO
                </small>


                <strong>
                  CORE
                </strong>


                <b>
                  TENANT-AWARE
                </b>

              </motion.div>

            </div>


            {/* ================================================
                CORE TELEMETRY
            ================================================ */}

            <div className="eth-arch-v5-core-telemetry">

              <div>

                <span>
                  TENANCY
                </span>

                <strong>
                  SCOPED
                </strong>

              </div>


              <div>

                <span>
                  GAME LAYER
                </span>

                <strong>
                  FIRST-PARTY
                </strong>

              </div>


              <div>

                <span>
                  MONEY
                </span>

                <strong>
                  LEDGERED
                </strong>

              </div>


              <div>

                <span>
                  EVIDENCE
                </span>

                <strong>
                  ATTRIBUTABLE
                </strong>

              </div>

            </div>

          </ArchReveal>



          {/* ==================================================
              RIGHT DETAIL PANEL
          ================================================== */}

          <ArchReveal
            direction="right"
            className="eth-arch-v5-detail-panel"
          >

            <div className="eth-arch-v5-detail-panel-top">

              <span>
                SELECTED LAYER
              </span>


              <b>
                {
                  activeLayer.number
                } / 06
              </b>

            </div>


            <AnimatePresence
              mode="wait"
            >

              <motion.div
                key={
                  activeLayer.id
                }

                className={
                  `eth-arch-v5-detail-content eth-arch-v5-detail-content--${activeLayer.accent}`
                }

                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity:
                          0,

                        x:
                          30,

                        filter:
                          "blur(6px)"
                      }
                }

                animate={{
                  opacity:
                    1,

                  x:
                    0,

                  filter:
                    "blur(0px)"
                }}

                exit={{
                  opacity:
                    0,

                  x:
                    -20,

                  filter:
                    "blur(5px)"
                }}

                transition={{
                  duration:
                    0.32
                }}
              >

                <div className="eth-arch-v5-detail-identity">

                  <div className="eth-arch-v5-detail-icon">

                    <ActiveIcon
                      size={
                        24
                      }
                    />

                  </div>


                  <div>

                    <span>
                      {
                        activeLayer.kicker
                      }
                    </span>

                    <h3>
                      {
                        activeLayer.title
                      }
                    </h3>

                  </div>

                </div>


                <h4>
                  {
                    activeLayer.headline
                  }
                </h4>


                <p className="eth-arch-v5-detail-description">

                  {
                    activeLayer.description
                  }

                </p>


                {/* ============================================
                    SIGNALS
                ============================================ */}

                <div className="eth-arch-v5-detail-signals">

                  {
                    activeLayer.signals.map(
                      signal => (

                        <div
                          key={
                            signal.label
                          }
                        >

                          <span>
                            {
                              signal.label
                            }
                          </span>

                          <strong>
                            {
                              signal.value
                            }
                          </strong>

                        </div>

                      )
                    )
                  }

                </div>


                {/* ============================================
                    CAPABILITIES
                ============================================ */}

                <div className="eth-arch-v5-capability-heading">

                  <span>
                    CAPABILITIES
                  </span>

                  <small>
                    INCLUDED IN THIS LAYER
                  </small>

                </div>


                <div className="eth-arch-v5-capabilities">

                  {
                    activeLayer.capabilities.map(
                      capability => (

                        <div
                          key={
                            capability
                          }
                        >

                          <span>

                            <Check
                              size={
                                12
                              }
                              strokeWidth={
                                3
                              }
                            />

                          </span>

                          {
                            capability
                          }

                        </div>

                      )
                    )
                  }

                </div>


                {/* ============================================
                    OUTPUT
                ============================================ */}

                <div className="eth-arch-v5-detail-output">

                  <div className="eth-arch-v5-detail-output-icon">

                    <Route
                      size={
                        17
                      }
                    />

                  </div>


                  <div>

                    <span>
                      SYSTEM OUTPUT
                    </span>

                    <p>
                      {
                        activeLayer.output
                      }
                    </p>

                  </div>

                </div>

              </motion.div>

            </AnimatePresence>

          </ArchReveal>

        </div>



        {/* ====================================================
            DATA FLOW STORY
        ==================================================== */}

        <ArchReveal
          direction="left"
          className="eth-arch-v5-flow-section"
        >

          <div className="eth-arch-v5-flow-copy">

            <span className="eth-arch-v5-eyebrow-simple">
              CONNECTED OPERATIONS
            </span>


            <h3>

              A casino action should not disappear into a black box.

            </h3>


            <p>

              The architecture is designed so important activity can move
              through identifiable systems: a player enters the product, a
              game produces an event, financial state changes, controls observe
              the activity, and the operator receives the evidence needed to
              understand what happened.

            </p>

          </div>


          <div className="eth-arch-v5-flow">

            <div className="eth-arch-v5-flow-track" />


            {
              [
                {
                  icon:
                    Globe2,

                  label:
                    "PLAYER",

                  value:
                    "ACTION"
                },

                {
                  icon:
                    Gamepad2,

                  label:
                    "GAME",

                  value:
                    "EVENT"
                },

                {
                  icon:
                    CircleDollarSign,

                  label:
                    "MONEY",

                  value:
                    "SETTLEMENT"
                },

                {
                  icon:
                    ShieldCheck,

                  label:
                    "CONTROL",

                  value:
                    "EVIDENCE"
                },

                {
                  icon:
                    Gauge,

                  label:
                    "OPERATOR",

                  value:
                    "INSIGHT"
                }

              ].map(
                (
                  step,
                  index
                ) => {

                  const Icon =
                    step.icon;


                  return (

                    <motion.div
                      key={
                        step.label
                      }

                      className="eth-arch-v5-flow-step"

                      initial={
                        reduceMotion
                          ? false
                          : {
                              opacity:
                                0,

                              y:
                                30
                            }
                      }

                      whileInView={{
                        opacity:
                          1,

                        y:
                          0
                      }}

                      viewport={{
                        once:
                          true
                      }}

                      transition={{
                        delay:
                          index *
                          0.08
                      }}
                    >

                      <div className="eth-arch-v5-flow-step-icon">

                        <Icon
                          size={
                            19
                          }
                        />

                      </div>


                      <span>
                        {
                          step.label
                        }
                      </span>

                      <strong>
                        {
                          step.value
                        }
                      </strong>

                    </motion.div>

                  );

                }
              )
            }


            <motion.span
              className="eth-arch-v5-flow-packet"

              animate={
                reduceMotion
                  ? {}
                  : {
                      left: [
                        "1%",
                        "96%"
                      ]
                    }
              }

              transition={{
                duration:
                  5.2,

                repeat:
                  Infinity,

                ease:
                  "linear"
              }}
            />

          </div>

        </ArchReveal>



        {/* ====================================================
            ARCHITECTURE MANIFESTO
        ==================================================== */}

        <ArchReveal
          direction="right"
          className="eth-arch-v5-manifesto"
        >

          <div className="eth-arch-v5-manifesto-icon">

            <Sparkles
              size={
                22
              }
            />

          </div>


          <div>

            <span>
              ETHOSINO ARCHITECTURE PRINCIPLE
            </span>


            <h3>

              The operator should control the business.
              <strong>
                {" "}
                Critical systems should explain themselves.
              </strong>

            </h3>

          </div>

        </ArchReveal>



        {/* ====================================================
            CTA
        ==================================================== */}

        <ArchReveal
          direction="left"
          className="eth-arch-v5-bottom"
        >

          <div className="eth-arch-v5-bottom-copy">

            <span>
              GO DEEPER
            </span>


            <h3>
              See how the pieces behave as one platform.
            </h3>


            <p>
              Walk through tenant isolation, first-party games, wallet and
              ledger behavior, fairness evidence, operator administration and
              the intelligence layer in a live environment.
            </p>

          </div>


          <div className="eth-arch-v5-bottom-actions">

            <Link
              className="eth-arch-v5-button eth-arch-v5-button--primary"
              to="/book-demo"
            >

              <span>
                Request Infrastructure Demo
              </span>

              <ArrowRight
                size={
                  17
                }
              />

            </Link>


            <Link
              className="eth-arch-v5-button eth-arch-v5-button--secondary"
              to="/info/multi-tenant/infrastructure"
            >

              <span>
                Explore Technology
              </span>

              <ArrowRight
                size={
                  17
                }
              />

            </Link>

          </div>

        </ArchReveal>

      </div>

    </section>

  );

};


export default EthosinoArchitectureSection;